import { Button, DialogBody, DialogFooter, DialogHeader, LoadingContainer, openModal, RenderProps } from '@ff-it/ui';
import { ReactElement, useState } from 'react';
import { Form, Submit } from '@ff-it/form';
import useSWR from 'swr';
import type { EntityContextType } from 'components/scenes';
import type { ActionContext, SelectionState } from './types';
import type { RequestFailure } from '@ff-it/api';

import { fetcher } from 'services';
import { checkActionError } from 'utilities';
import { ActionContainer } from './ActionContainer';
import { toast } from 'react-toastify';
import invariant from 'tiny-invariant';
import { dialogClassName } from './style.css';

type CalatogActionDialogProps<E = any> = RenderProps<NonNullable<SelectionState>> & {
  ctx: EntityContextType<E>;
};

export function CalatogActionDialog({ onDismiss, ctx, onSubmit }: CalatogActionDialogProps): ReactElement {
  const url = `${ctx.endpoint}catalog/`;

  const { data: actionContext, isLoading, error } = useSWR<ActionContext>({ url, method: 'GET' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [mutationError, setMutationError] = useState<any>(undefined);
  const [selectionState, setSelectionState] = useState<SelectionState>(null);

  const submitHanlder = async () => {
    setIsSubmitting(true);

    try {
      await fetcher({ url, method: 'POST', body: selectionState });

      setIsSubmitting(false);
      toast.success('Action has been executed.');
      invariant(selectionState);
      onSubmit(selectionState);
      // We assume current selection state is what we did
    } catch (err) {
      if (!checkActionError(err as RequestFailure<any>)) {
        setMutationError((err as RequestFailure<any>).error);
      }
      setIsSubmitting(false);
    }
  };

  if (mutationError) {
    return mutationError;
  }
  if (error) {
    throw error;
  }

  return (
    <LoadingContainer loading={isLoading || isSubmitting} className="flex-grow-1 flex-column d-flex">
      {actionContext && (
        <Form onSubmit={submitHanlder}>
          <DialogHeader title="Catalog actions" />
          <DialogBody>
            <ActionContainer
              actionContext={actionContext}
              selectionState={selectionState}
              setSelectionState={setSelectionState}
            />
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="secondary" size="sm" onClick={onDismiss}>
              Cancel
            </Button>
            <Submit disabled={!selectionState} className="ml-auto" />
          </DialogFooter>
        </Form>
      )}
    </LoadingContainer>
  );
}

export async function openCatalogAction<E = any>(
  ctx: EntityContextType<E>,
  onSuccess: (res: NonNullable<SelectionState>) => void,
) {
  return await openModal<NonNullable<SelectionState>>(
    (dialogProps) => <CalatogActionDialog {...dialogProps} ctx={ctx} />,
    { testId: 'catalog-action', className: dialogClassName },
  ).then(onSuccess);
}
